import { useCallback, useEffect, useState } from "react";
import { supabase, rowToCamel, rowToSnake } from "@/lib/supabase";

/**
 * Supabase-backed single-record hook (for settings tables with one row).
 * Seeds the row on first load if missing.
 */
export function useSupabaseRecord<T extends { id: string }>(table: string, seed: T) {
  const [record, setRecord] = useState<T>(seed);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data, error: err } = await supabase
          .from(table)
          .select("*")
          .limit(1)
          .maybeSingle();

        if (err) throw err;

        if (!active) return;

        if (data) {
          setRecord(rowToCamel<T>(data));
        } else {
          await supabase.from(table).upsert(rowToSnake(seed as unknown as Record<string, unknown>));
          setRecord(seed);
        }
      } catch (e) {
        if (active) {
          setError(e instanceof Error ? e.message : "Failed to load data");
          setRecord(seed);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // seed is a stable module-level constant from mocks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  const update = useCallback(
    async (patch: Partial<T>) => {
      setRecord((prev) => ({ ...prev, ...patch }));
      try {
        await supabase
          .from(table)
          .update(rowToSnake(patch as Record<string, unknown>))
          .eq("id", seed.id);
      } catch {
        // ignore
      }
    },
    [table, seed.id],
  );

  return { record, loading, error, update };
}